import React from "react";
import { Table } from "react-bootstrap";

import {
  BessHoraResultado,
  BessResumoDiario,
  BessResumoGeral,
} from "../../types";
import { formatBrazilianCurrency } from "../../../../utils/utilityFunctions";

interface BessDailyTableProps {
  diaria: BessResumoDiario[];
  horaria: BessHoraResultado[];
  resumo: BessResumoGeral;
}

interface DailySourceBreakdown {
  cargaRede: number;
  cargaSolar: number;
  injetada: number;
}

/** O resumo diário do backend não separa a carga por fonte nem a injeção —
 * esses campos só existem hora a hora, então agrega-se aqui por dia. */
export function aggregateHourlyByDay(
  horaria: BessHoraResultado[]
): Record<string, DailySourceBreakdown> {
  return horaria.reduce<Record<string, DailySourceBreakdown>>((acc, row) => {
    const day =
      acc[row.data] ??
      (acc[row.data] = { cargaRede: 0, cargaSolar: 0, injetada: 0 });
    day.cargaRede += row.energiaCargaRedeAc;
    day.cargaSolar += row.energiaCargaSolarAc;
    day.injetada += row.energiaInjetadaKwh;
    return acc;
  }, {});
}

const EMPTY_BREAKDOWN: DailySourceBreakdown = {
  cargaRede: 0,
  cargaSolar: 0,
  injetada: 0,
};

const formatKwh = (value: number): string =>
  Math.round(value).toLocaleString("pt-BR");

const CurrencyCell: React.FC<{ value: number }> = ({ value }) => {
  const formatted = formatBrazilianCurrency(value);
  if (value < 0) {
    return <span style={{ color: "#f91010" }}>{formatted}</span>;
  }
  if (value > 0) {
    return <span style={{ color: "#1fa824" }}>{formatted}</span>;
  }
  return <>{formatted}</>;
};

const isWeekend = (data: string): boolean => {
  const [ano, mes, dia] = data.split("-").map(Number);
  const weekday = new Date(ano, mes - 1, dia).getDay();
  return weekday === 0 || weekday === 6;
};

const formatDayLabel = (data: string): string => {
  const [, mes, dia] = data.split("-");
  return `${dia}/${mes}`;
};

export const BessDailyTable: React.FC<BessDailyTableProps> = ({
  diaria,
  horaria,
  resumo,
}) => {
  const breakdownByDay = aggregateHourlyByDay(horaria);

  return (
    <div style={{ overflow: "auto", maxHeight: 420 }}>
      <Table>
        <thead>
          <tr>
            <th>Dia</th>
            <th>Carga rede (kWh)</th>
            <th>Carga solar (kWh)</th>
            <th>Entregue (kWh)</th>
            <th>Injetada (kWh)</th>
            <th>Custo carga (R$)</th>
            <th>Ganho ponta (R$)</th>
            <th>MCP (R$)</th>
            <th>Lucro (R$)</th>
          </tr>
        </thead>
        <tbody>
          {diaria.map((dia) => {
            const weekend = isWeekend(dia.data);
            const dayLabel = weekend
              ? `${formatDayLabel(dia.data)} · fds`
              : formatDayLabel(dia.data);
            const breakdown = breakdownByDay[dia.data] ?? EMPTY_BREAKDOWN;

            return (
              <tr
                key={dia.data}
                style={weekend ? { color: "#7e7e82" } : undefined}
              >
                <td>{dayLabel}</td>
                <td>{formatKwh(breakdown.cargaRede)}</td>
                <td>{formatKwh(breakdown.cargaSolar)}</td>
                <td>{formatKwh(dia.energiaEntregueAc)}</td>
                <td>{formatKwh(breakdown.injetada)}</td>
                <td>
                  <CurrencyCell value={dia.custoCarga} />
                </td>
                <td>
                  <CurrencyCell value={dia.ganhoDescarga} />
                </td>
                <td>
                  <CurrencyCell value={dia.mcpDelta} />
                </td>
                <td>
                  <CurrencyCell value={dia.lucro} />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: "bold" }}>
            <td>Total do mês</td>
            <td>{formatKwh(resumo.energiaCarregadaRedeKwh)}</td>
            <td>{formatKwh(resumo.energiaCarregadaSolarKwh)}</td>
            <td>{formatKwh(resumo.energiaEntregueAcKwh)}</td>
            <td>{formatKwh(resumo.energiaInjetadaKwh)}</td>
            <td>
              <CurrencyCell value={resumo.custoCargaRede} />
            </td>
            <td>
              <CurrencyCell value={resumo.ganhoDescarga} />
            </td>
            <td>
              <CurrencyCell value={resumo.mcpDelta} />
            </td>
            <td>
              <CurrencyCell value={resumo.lucroTotal} />
            </td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};
